import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

export interface SpeechRecognitionError {
  error: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class SpeechRecognitionService {
  private recognition: any;
  private isListening = false;
  private isSupported = false;

  // BehaviorSubjects for reactive updates
  private _isListeningSubject = new BehaviorSubject<boolean>(false);
  private _resultSubject = new BehaviorSubject<SpeechRecognitionResult | null>(null);
  private _errorSubject = new BehaviorSubject<SpeechRecognitionError | null>(null);
  private _isSupportedSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public isListening$ = this._isListeningSubject.asObservable();
  public result$ = this._resultSubject.asObservable();
  public error$ = this._errorSubject.asObservable();
  public isSupported$ = this._isSupportedSubject.asObservable();

  constructor() {
    this.initializeSpeechRecognition();
  }

  private initializeSpeechRecognition(): void {
    // Check for browser support
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser');
      this._isSupportedSubject.next(false);
      return;
    }

    this.isSupported = true;
    this._isSupportedSubject.next(true);

    // Initialize the recognition object
    this.recognition = new SpeechRecognition();
    
    // Configure recognition settings
    this.recognition.continuous = false; // Stop after each utterance
    this.recognition.interimResults = true; // Get interim results
    this.recognition.lang = 'en-US'; // Default language
    this.recognition.maxAlternatives = 1;

    // Set up event handlers
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.recognition) return;

    // Handle successful recognition
    this.recognition.onresult = (event: any) => {
      const result = event.results[event.results.length - 1];
      const transcript = result[0].transcript;
      const confidence = result[0].confidence;
      const isFinal = result.isFinal;

      const speechResult: SpeechRecognitionResult = {
        transcript: transcript.trim(),
        confidence: confidence || 0,
        isFinal: isFinal
      };

      this._resultSubject.next(speechResult);

      // Only auto-stop if not in continuous mode
      // In continuous mode, the browser will keep listening automatically
      if (isFinal && !this.recognition.continuous) {
        this.stopListening();
      }
    };

    // Handle recognition start
    this.recognition.onstart = () => {
      console.log('Speech recognition started');
      // In continuous mode, onstart fires on restart, so always update state
      if (this.recognition.continuous) {
        // Continuous mode: always update state on start (handles restarts)
        this.isListening = true;
        this._isListeningSubject.next(true);
        this._errorSubject.next(null); // Clear any previous errors
      } else {
        // Non-continuous mode: only set if not already listening
        if (!this.isListening) {
          this.isListening = true;
          this._isListeningSubject.next(true);
          this._errorSubject.next(null); // Clear any previous errors
        }
      }
    };

    // Handle recognition end
    this.recognition.onend = () => {
      console.log('Speech recognition ended');
      // In continuous mode, manually restart listening to ensure it keeps going
      if (!this.recognition.continuous) {
        // Non-continuous mode: reset listening state when recognition ends
        if (this.isListening) {
          this.isListening = false;
          this._isListeningSubject.next(false);
        }
      } else {
        // Continuous mode: manually restart to ensure it keeps listening
        // Some browsers don't restart automatically reliably
        if (this.isListening) {
          console.log('Continuous mode: manually restarting recognition...');
          // Small delay to ensure clean restart
          setTimeout(() => {
            try {
              if (this.isListening && this.recognition) {
                this.recognition.start();
                console.log('Continuous mode: recognition restarted');
              }
            } catch (error: any) {
              // If restart fails (e.g., already started), that's okay
              if (error.message && !error.message.includes('already started')) {
                console.warn('Failed to restart in continuous mode:', error);
              }
            }
          }, 100);
        }
      }
    };

    // Handle recognition errors
    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      
      const error: SpeechRecognitionError = {
        error: event.error,
        message: this.getErrorMessage(event.error)
      };

      this._errorSubject.next(error);
      this.isListening = false;
      this._isListeningSubject.next(false);
    };

    // Handle no speech detected
    this.recognition.onnomatch = () => {
      console.log('No speech was recognized');
      const error: SpeechRecognitionError = {
        error: 'no-speech',
        message: 'No speech was detected. Please try again.'
      };
      this._errorSubject.next(error);
    };
  }

  private getErrorMessage(error: string): string {
    switch (error) {
      case 'no-speech':
        return 'No speech was detected. Please try again.';
      case 'audio-capture':
        return 'No microphone was found. Please ensure a microphone is connected.';
      case 'not-allowed':
        return 'Microphone permission was denied. Please allow microphone access.';
      case 'network':
        return 'Network error occurred. Please check your internet connection.';
      case 'aborted':
        return 'Speech recognition was aborted.';
      case 'service-not-allowed':
        return 'Speech recognition service is not allowed.';
      default:
        return `Speech recognition error: ${error}`;
    }
  }

  /**
   * Start listening for speech input
   */
  public startListening(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSupported) {
        const error: SpeechRecognitionError = {
          error: 'not-supported',
          message: 'Speech recognition is not supported in this browser.'
        };
        this._errorSubject.next(error);
        reject(error);
        return;
      }

      if (this.isListening) {
        console.log('Already listening, stopping first...');
        this.stopListening();
        // Wait a bit before starting again
        setTimeout(() => {
          this.startListening().then(resolve).catch(reject);
        }, 300);
        return;
      }

      try {
        // Clear previous results and errors
        this._resultSubject.next(null);
        this._errorSubject.next(null);

        // Start recognition
        this.recognition.start();
        
        // Set up timeout to detect if start fails silently
        // We'll check after a short delay if the listening state was set
        setTimeout(() => {
          if (!this.isListening) {
            console.warn('Speech recognition may have failed to start - listening state not set');
            // Don't reject here, let the error handler deal with it
            // But log it for debugging
          }
        }, 1500);

        resolve();
      } catch (error: any) {
        console.error('Failed to start speech recognition:', error);
        this.isListening = false;
        this._isListeningSubject.next(false);
        const speechError: SpeechRecognitionError = {
          error: 'start-failed',
          message: 'Failed to start speech recognition: ' + (error.message || 'Unknown error')
        };
        this._errorSubject.next(speechError);
        reject(speechError);
      }
    });
  }

  /**
   * Stop listening for speech input
   */
  public stopListening(): void {
    if (!this.isSupported) {
      return;
    }

    // Force stop by immediately updating state
    const wasListening = this.isListening;
    this.isListening = false;
    this._isListeningSubject.next(false);

    if (wasListening && this.recognition) {
      try {
        // Try graceful stop first
        this.recognition.stop();
        console.log('Stopping speech recognition (graceful)...');
      } catch (error) {
        console.warn('Graceful stop failed, trying abort:', error);
        try {
          // If stop fails, try abort
          this.recognition.abort();
          console.log('Aborting speech recognition...');
        } catch (abortError) {
          console.error('Both stop and abort failed:', abortError);
          // State already updated above, so we're good
        }
      }
    } else {
      console.log('Speech recognition was not listening, state reset');
    }
  }

  /**
   * Abort speech recognition immediately
   */
  public abortListening(): void {
    if (!this.isSupported) {
      return;
    }

    // Force stop by immediately updating state
    const wasListening = this.isListening;
    this.isListening = false;
    this._isListeningSubject.next(false);

    if (wasListening && this.recognition) {
      try {
        // Abort immediately (more forceful than stop)
        this.recognition.abort();
        console.log('Aborting speech recognition immediately...');
      } catch (error) {
        console.warn('Abort failed, trying stop:', error);
        try {
          // Fallback to stop if abort fails
          this.recognition.stop();
          console.log('Stopping speech recognition...');
        } catch (stopError) {
          console.error('Both abort and stop failed:', stopError);
          // State already updated above, so we're good
        }
      }
    } else {
      console.log('Speech recognition was not listening, state reset');
    }
  }

  /**
   * Set the language for speech recognition
   */
  public setLanguage(lang: string): void {
    if (this.recognition) {
      this.recognition.lang = lang;
    }
  }

  /**
   * Set continuous listening mode
   */
  public setContinuous(continuous: boolean): void {
    if (this.recognition) {
      this.recognition.continuous = continuous;
    }
  }

  /**
   * Set interim results
   */
  public setInterimResults(interim: boolean): void {
    if (this.recognition) {
      this.recognition.interimResults = interim;
    }
  }

  /**
   * Get current listening state
   */
  public getIsListening(): boolean {
    return this.isListening;
  }

  /**
   * Get browser support status
   */
  public getIsSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Request microphone permission
   */
  public async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately as we just needed permission
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      const speechError: SpeechRecognitionError = {
        error: 'permission-denied',
        message: 'Microphone permission was denied. Please allow microphone access in your browser settings.'
      };
      this._errorSubject.next(speechError);
      return false;
    }
  }
}
